import React, { useMemo, useCallback } from "react";
import { FocusScope } from "react-aria";
import { Link } from "react-router-dom";

/**
 * Renderiza el contenido del menú desplegable de Recursos del Header General y publico.
 *
 * El componente muestra las opciones de navegación disponibles para la sección
 * de Recursos y administra el cierre del menú al seleccionar una opción o al
 * presionar la tecla <kbd>Escape</kbd>. Además, utiliza `FocusScope` para
 * contener y restaurar el foco, mejorando la accesibilidad durante la
 * navegación mediante teclado.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.setOpenDropdown - Función que actualiza el estado de apertura del menú.
 * @param {boolean} props.openDropdown - Indica si el menú desplegable se encuentra abierto.
 * @param {boolean} props.isAuthenticated - Indica si existe una sesión autenticada.
 * @param {Object} props.textosInterfaz - Objeto con los textos traducidos utilizados en la interfaz.
 *
 * @returns {JSX.Element|null} HGDropdownResources
 */
export const HGDropdownResources = ({
    setOpenDropdown,
    openDropdown,
    isAuthenticated,
    textosInterfaz,
}) => {
    const closeDropdown = useCallback(() => {
        setOpenDropdown(false);
    }, [setOpenDropdown]);

    const subSecciones = useMemo(
        () => [
            {
                id: "resources",
                link: "/comunicacion",
                name: textosInterfaz?.comunicacion ?? "Comunicación",
            },
            // {
            //     id: "pedagogico",
            //     link: "/centro-pedagogico",
            //     name: textosInterfaz?.centro_pedagogico ?? "Centro pedagógico",
            // },
            {
                id: "blog",
                link: "/blog",
                name: textosInterfaz?.blog ?? "Blog",
            },
            {
                id: isAuthenticated ? "help" : "help-logout",
                link: isAuthenticated ? "/ayuda" : "/ayuda-sin-login",
                name: textosInterfaz?.ayuda ?? "Ayuda",
            },
            {
                id: "apps",
                link: "https://makemake.app/home?platform=macos",
                name: "Apps",
                external: true,
            },
        ],
        [isAuthenticated, textosInterfaz]
    );

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === "Escape") {
                closeDropdown();
            }
        },
        [closeDropdown]
    );

    if (!openDropdown) {
        return null;
    }

    return (
        <FocusScope contain restoreFocus autoFocus>
            <ul
                role="menu"
                onKeyDown={handleKeyDown}
            >
                {subSecciones.map(({ id, link, name, external }) => (
                    <li
                        key={id}
                        className="activeMode body-m"
                        role="none"
                    >
                        {external ? (
                            <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="focusable-red"
                                role="menuitem"
                                onClick={closeDropdown}
                            >
                                {name}
                            </a>
                        ) : (
                            <Link
                                to={link}
                                className="focusable-red"
                                role="menuitem"
                                onClick={closeDropdown}
                            >
                                {name}
                            </Link>
                        )}
                    </li>
                ))}

                <li
                    className="close-options activeMode"
                    role="none"
                >
                    <button
                        type="button"
                        className="w-100 focusable-red"
                        onClick={closeDropdown}
                        aria-label={
                            textosInterfaz?.button_close_list_alt ??
                            "Cerrar la lista desplegable"
                        }
                    >
                        {textosInterfaz?.button_close_list ?? "Cerrar"}
                    </button>
                </li>
            </ul>
        </FocusScope>
    );
};